// SEO data + shared FAQ content for the Services page.
import { brand } from "@/config/brand";
const SITE_URL = brand.url;

export const FAQ_CATEGORIES = [
  {
    label: 'Getting Started',
    faqs: [
      { q: "I don't know anything about tech. Will this work for me?", a: "Yes — that's exactly why we exist. We handle all the technical heavy lifting so you don't have to. We explain everything in plain English, no jargon, no condescension. If you can send an email, you can work with us." },
      { q: 'Do you only work with large companies?', a: "Not at all. We work with small businesses, tradespeople, and new businesses. We build scalable systems that fit your current size and budget, with room to grow as you do." },
      { q: 'What happens in the first consultation?', a: "We have a relaxed 30-minute call to understand your business, your goals, and where you're currently stuck. There's no hard sell — just an honest conversation about whether and how we can help." },
      { q: 'Do I need to have a clear brief before contacting you?', a: "Absolutely not. Many of our best client relationships started with 'I know something needs to change but I'm not sure what.' That's what we're here to figure out together." },
    ],
  },
  {
    label: 'Websites & Ownership',
    faqs: [
      { q: 'Do I own my website and domain?', a: "100%. We set it up, but you hold the keys. No hostage situations. If you ever want to move on (though we hope you won't), you take everything with you — code, content, domain, the lot." },
      { q: 'How long does a new website take?', a: "Using our AI-assisted workflows, we can often get a stunning, high-converting site live in a matter of weeks rather than months. The exact timeline depends on complexity, but we'll give you a realistic estimate upfront." },
      { q: 'Can you work with my existing website?', a: "Yes. We can audit, improve, or rebuild your existing site depending on what makes most sense. We'll give you an honest assessment rather than pushing you towards a full rebuild if it isn't needed." },
      { q: 'Will my website work on mobile?', a: "Every site we build is mobile-first and tested across devices. With over 60% of web traffic now on mobile, this isn't optional — it's the baseline." },
    ],
  },
  {
    label: 'Pricing & Process',
    faqs: [
      { q: 'How much does it cost?', a: "We publish guide prices on our services page — websites from £500, Vision to Launch from £950, app creation from £1,500, and ongoing support at £50–£75/hr. Marketing is scoped after a free assessment as every brief is different. We always give you a clear, fixed service agreement before any work begins — no hidden fees, no surprise invoices." },
      { q: 'Do you offer payment plans?', a: "Yes. For larger projects we typically split payments into milestones — a deposit to start, a payment at a mid-point, and the final balance on delivery. We can discuss what works for your cash flow." },
      { q: 'What does the ongoing support look like?', a: "After launch, we offer monthly retainers covering updates, performance monitoring, and strategic advice. You can also opt for ad-hoc support if you prefer not to commit to a monthly plan." },
      { q: "How do I know I'm getting value for money?", a: "We set clear goals at the start of every engagement and report against them. You'll always know what we're doing, why we're doing it, and what result it's producing." },
    ],
  },
  {
    label: 'Digital Marketing & Strategy',
    faqs: [
      { q: 'Can you help me get found on Google?', a: "Yes. SEO, AEO, and GEO are built into everything we do — from site structure and page speed to content strategy and local search. We don't promise overnight rankings, but we do build sustainable, long-term visibility." },
      { q: 'Do you run social media for businesses?', a: "We can help you build a social media strategy and create content, but we focus on the channels that actually drive results for your business rather than being everywhere at once." },
      { q: "What's the difference between digital strategy and just 'doing marketing'?", a: "Most businesses jump straight into tactics — posting on Instagram, running ads — without a clear strategy. We start with your business goals and work backwards to identify which digital activities will actually move the needle." },
    ],
  },
];

export const servicesItemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  '@id': `${SITE_URL}/services#servicelist`,
  name: 'Digitally Baffled LTD — Digital Transformation Services',
  url: `${SITE_URL}/services`,
  description: 'Full range of digital transformation services for UK small businesses, tradespeople, and new businesses — from web design and Google Ads to ongoing support and business launch packages.',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Service',
        name: 'AI Web & App Design',
        description: 'We build you a website or app, or improve your existing one. Using AI-assisted workflows we go from brief to live in weeks, not months — without cutting corners on quality.',
        url: `${SITE_URL}/services/ai-web-app-design`,
        provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Digitally Baffled LTD', url: SITE_URL },
        areaServed: { '@type': 'Country', name: 'United Kingdom' },
        serviceType: 'Digital Transformation Consultancy',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'GBP',
          price: '500',
          priceSpecification: { '@type': 'PriceSpecification', minPrice: '500', priceCurrency: 'GBP', description: 'Websites from £500+. Apps from £1,500+.' },
          description: 'Websites from £500+. Apps from £1,500+.',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Service',
        name: 'Marketing',
        description: 'We set up your advertising no matter the platform — PPC, Google Ads, Facebook Ads, Instagram Ads, and more. Campaigns built around real business goals, not vanity metrics.',
        url: `${SITE_URL}/services/marketing`,
        provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Digitally Baffled LTD', url: SITE_URL },
        areaServed: { '@type': 'Country', name: 'United Kingdom' },
        serviceType: 'Digital Transformation Consultancy',
        offers: { '@type': 'Offer', description: 'Priced after free assessment' },
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Service',
        name: 'Ongoing Support',
        description: 'Website updates, design and content changes, technical support, SEO, AEO & GEO improvements, account management, and general digital help — everything you need to keep growing after launch.',
        url: `${SITE_URL}/services/ongoing-support`,
        provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Digitally Baffled LTD', url: SITE_URL },
        areaServed: { '@type': 'Country', name: 'United Kingdom' },
        serviceType: 'Digital Transformation Consultancy',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'GBP',
          priceSpecification: { '@type': 'PriceSpecification', minPrice: '50', maxPrice: '75', priceCurrency: 'GBP', unitText: 'HOUR', description: '£50–£75 per hour' },
          description: '£50–£75 per hour',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Service',
        name: 'Vision to Launch',
        description: 'Brand direction, visual identity, social media branding, SEO, AEO & GEO foundations, Google Business setup, company registration support, and a full launch checklist — everything to go from idea to live.',
        url: `${SITE_URL}/services/vision-to-launch`,
        provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Digitally Baffled LTD', url: SITE_URL },
        areaServed: { '@type': 'Country', name: 'United Kingdom' },
        serviceType: 'Digital Transformation Consultancy',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'GBP',
          price: '950',
          priceSpecification: { '@type': 'PriceSpecification', minPrice: '950', priceCurrency: 'GBP', description: 'From £950+' },
          description: 'From £950+',
        },
      },
    },
  ],
};

export const servicesFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_CATEGORIES.flatMap(cat => cat.faqs).map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};
